import { useEffect } from 'react'
import CarePlanTaskEditorRow from './CarePlanTaskEditorRow'
import { IconButton, Typography, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { anyIncompleteTask } from './utils'
import { CarePlanModalTask, mapCarePlanPreviewTasksToTasks } from './useCarePlanModal'
import { format } from 'date-fns'
import { ISO_DATE_FORMAT } from '~/shared/lib/date'
import { CarePlanTemplatesPreviewRetrieveResponse } from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'

interface ITasksFormProps {
  patient: PatientUser
  me: RootState['me']
  previewData?: CarePlanTemplatesPreviewRetrieveResponse
  setAllTemplateTasksComplete: (arg0: boolean) => void
  saving: boolean
  tasks: CarePlanModalTask[]
  setTasks: (arg0: CarePlanModalTask[]) => void
}

// Given a preview of the tasks that *would* be created from a template
// allow the user to view and edit those task data
const EditTemplateTasks = (props: ITasksFormProps) => {
  const { setAllTemplateTasksComplete, saving, tasks, setTasks } = props
  const defaultAssigneeGroup = props.me.assignee_group?.id

  useEffect(() => {
    setAllTemplateTasksComplete(!anyIncompleteTask(tasks))
  }, [tasks, setAllTemplateTasksComplete])

  useEffect(() => {
    // Transform the preview data to editable tasks
    if (!props.previewData?.task_previews) {
      return
    }
    setTasks(
      mapCarePlanPreviewTasksToTasks(props.previewData.task_previews, {
        defaultAssigneeGroup: defaultAssigneeGroup,
      }),
    )
  }, [props.previewData, setTasks, defaultAssigneeGroup])

  const addTask = () => {
    const newTasks: CarePlanModalTask[] = [
      ...tasks,
      {
        title: '',
        due_date: format(new Date(), ISO_DATE_FORMAT),
        owner_group: defaultAssigneeGroup,
        priority: 0,
        is_complete: false,
      },
    ]
    setTasks(newTasks)
  }

  /**
   * @param {number} i - index of the array of tasks
   */
  const updateTask = (i: number, update: Partial<CarePlanModalTask>) => {
    const newTasks = tasks.map((task, index) => (index === i ? { ...task, ...update } : task))
    setTasks(newTasks)
  }

  const removeTask = (i: number) => {
    const newTasks = [...tasks]
    newTasks.splice(i, 1)
    setTasks(newTasks)
  }

  const taskEditorRows = tasks.map((task, i) => {
    return (
      <Box key={`task_${i}`} sx={{ mb: 3 }}>
        <CarePlanTaskEditorRow
          i={i}
          task={task}
          disabledEdit={false}
          disabledRemove={false}
          onChange={updateTask}
          onRemove={removeTask}
          patient={props.patient}
        />
      </Box>
    )
  })

  const lastTask = tasks.at(-1)
  const editingLastTask = lastTask !== undefined && (!lastTask.title || !lastTask.due_date)
  const disabledAdd = editingLastTask || saving

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ mb: 1 }}>Tasks</Typography>
      {taskEditorRows}
      <IconButton disabled={disabledAdd} onClick={addTask} sx={{ p: 0.5 }}>
        <AddIcon />
      </IconButton>
    </Box>
  )
}

export default EditTemplateTasks
